import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { HiOutlineExclamation } from "react-icons/hi";

import API from "../api";
import { capture } from "../instrument";
import { FIELD_LABELS } from "./SyncInfoBanner";
import { TRIGGER_TYPE_LABELS } from "../utils";

const humanizeTrigger = (trigger) => {
  const typeKey = trigger?.type === "INFO" ? `INFO_${trigger.field}` : trigger?.type;
  const label = TRIGGER_TYPE_LABELS[typeKey] || typeKey || "Condition";
  const values = (trigger?.values || []).filter(Boolean);
  const prefix = trigger?.negate === true ? "SAUF " : "";
  if (trigger?.type === "ALWAYS") return `${prefix}${label}`;
  if (trigger?.type === "QUESTION") return `${prefix}${label} (condition sur une réponse)`;
  return `${prefix}${label} : ${values.join(", ") || "—"}`;
};

const humanizeTriggerGroups = (groups) => {
  if (!Array.isArray(groups) || groups.length === 0) return ["Aucune condition"];
  return groups.map((group, index) => {
    const operator = group?.operator === "OR" ? " OU " : " ET ";
    const triggers = (group?.triggers || []).map(humanizeTrigger).join(operator);
    return groups.length > 1 ? `Groupe ${index + 1} · ${triggers}` : triggers;
  });
};

const humanizeValue = (field, value) => {
  if (value === null || value === undefined) return "—";
  if (field === "triggerGroups") return humanizeTriggerGroups(value);
  if (field === "tubes") return value.length === 0 ? "Aucun tube" : value.map((tube) => `${tube.quantity} × ${tube.type}`).join(", ");
  if (field === "options") return value.length === 0 ? "Aucune option" : value.map((option) => option.code).join(", ");
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (typeof value === "string") return value || "(vide)";
  if (typeof value === "number") return String(value);
  if (Array.isArray(value) && value.every((item) => typeof item === "string")) return value.length === 0 ? "—" : value.join(", ");
  return null;
};

const tokenDiff = (oldText, newText) => {
  const a = oldText.split(/(\s+)/);
  const b = newText.split(/(\s+)/);
  let prefix = 0;
  while (prefix < a.length && prefix < b.length && a[prefix] === b[prefix]) prefix++;
  let suffix = 0;
  while (suffix < a.length - prefix && suffix < b.length - prefix && a[a.length - 1 - suffix] === b[b.length - 1 - suffix]) suffix++;
  return {
    removed: { pre: a.slice(0, prefix).join(""), mid: a.slice(prefix, a.length - suffix).join(""), post: a.slice(a.length - suffix).join("") },
    added: { pre: b.slice(0, prefix).join(""), mid: b.slice(prefix, b.length - suffix).join(""), post: b.slice(b.length - suffix).join("") },
  };
};

const DiffRow = ({ kind, parts, text }) => (
  <div className={`flex text-gray-700 ${kind === "removed" ? "bg-red-50" : "bg-green-50"}`}>
    <span className={`w-8 shrink-0 select-none py-1 text-center ${kind === "removed" ? "text-red-500" : "text-green-600"}`}>{kind === "removed" ? "−" : "+"}</span>
    <span className="min-w-0 flex-1 whitespace-pre-wrap py-1 pr-3">
      {parts ? (
        <>
          {parts.pre}
          {parts.mid && <span className={`rounded-sm px-0.5 ${kind === "removed" ? "bg-red-200/70 text-red-800" : "bg-green-200/70 text-green-800"}`}>{parts.mid}</span>}
          {parts.post}
        </>
      ) : (
        text
      )}
    </span>
  </div>
);

const DiffBlock = ({ field, oldValue, newValue }) => {
  const oldHumanized = humanizeValue(field, oldValue);
  const newHumanized = humanizeValue(field, newValue);

  const oldLines = Array.isArray(oldHumanized) ? oldHumanized : oldHumanized !== null ? [oldHumanized] : JSON.stringify(oldValue, null, 2).split("\n");
  const newLines = Array.isArray(newHumanized) ? newHumanized : newHumanized !== null ? [newHumanized] : JSON.stringify(newValue, null, 2).split("\n");
  const wordLevel = oldLines.length === 1 && newLines.length === 1 ? tokenDiff(oldLines[0], newLines[0]) : null;

  return (
    <div className="max-h-64 overflow-auto font-mono text-[13px]">
      {wordLevel ? (
        <>
          <DiffRow kind="removed" parts={wordLevel.removed} />
          <DiffRow kind="added" parts={wordLevel.added} />
        </>
      ) : (
        <>
          {oldLines.map((line, index) => (
            <DiffRow key={`removed-${index}`} kind="removed" text={line} />
          ))}
          {newLines.map((line, index) => (
            <DiffRow key={`added-${index}`} kind="added" text={line} />
          ))}
        </>
      )}
    </div>
  );
};

const SyncConflictBanner = ({ docId }) => {
  const queryClient = useQueryClient();

  const {
    data: overrides = [],
    isError,
  } = useQuery({
    queryKey: ["sync-overrides", docId],
    enabled: Boolean(docId),
    queryFn: async () => {
      const response = await API.get(`/sync/override?docId=${docId}`);
      if (!response.ok) {
        const error = new Error(response.message);
        capture(error);
        throw error;
      }
      return response.data;
    },
  });

  const keepMineMutation = useMutation({
    mutationFn: async (overrideId) => {
      const response = await API.put(`/sync/override/${overrideId}`, { conflict: false });
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    onSuccess: () => {
      toast.success("Conflit ignoré — votre valeur est conservée");
      queryClient.invalidateQueries({ queryKey: ["sync-overrides"] });
    },
    onError: (e) => toast.error(e.message || "Une erreur est survenue"),
  });

  const takeSourceMutation = useMutation({
    mutationFn: async (overrideId) => {
      const response = await API.delete(`/sync/override/${overrideId}`);
      if (!response.ok) throw new Error(response.message);
    },
    onSuccess: () => {
      toast.success("Mise à jour appliquée à la prochaine synchronisation");
      queryClient.invalidateQueries({ queryKey: ["sync-overrides"] });
    },
    onError: (e) => toast.error(e.message || "Une erreur est survenue"),
  });

  const conflicts = overrides.filter((override) => override.conflict);
  const isMutating = keepMineMutation.isPending || takeSourceMutation.isPending;

  if (!isError && conflicts.length === 0) return null;

  return (
    <div className="mb-6 space-y-3">
      {isError && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
          <HiOutlineExclamation className="h-4 w-4 shrink-0 text-red-500" />
          Impossible de vérifier les conflits de synchronisation — rechargez la page.
        </div>
      )}

      {conflicts.map((conflict) => (
        <div key={conflict._id} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5">
            <HiOutlineExclamation className="h-4 w-4 shrink-0 text-amber-500" />
            <span className="text-sm font-medium text-gray-900">{FIELD_LABELS[conflict.field] || conflict.field}</span>
            <span className="text-xs text-gray-500">modifié par la source depuis votre changement</span>
          </div>
          <DiffBlock field={conflict.field} oldValue={conflict.value} newValue={conflict.conflictSourceValue} />
          <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-4 py-2">
            <span className="text-xs text-gray-400">− votre valeur · + valeur de la source</span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={isMutating}
                onClick={() => keepMineMutation.mutate(conflict._id)}
                className="rounded-lg px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
              >
                Ignorer
              </button>
              <button
                type="button"
                disabled={isMutating}
                onClick={() => takeSourceMutation.mutate(conflict._id)}
                className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
              >
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SyncConflictBanner;
